import config from "csi/config";
import { Context } from "csi/def/Context";
import { Field } from "csi/def/Field";
import Relationship from "csi/def/Relationship";
import { FormView } from "csi/form/View";
import Widget from "csi/form/Widget";
import csi from "csi/main";
import reflection from "csi/reflection";
import { resolveNode } from "csi/service/nodeService";
import { pathCreate } from "csi/utility/pathUtility";
import xml from "csi/xml";
import declare from "dojo/_base/declare";
import lang from "dojo/_base/lang";
import ProductSelectProductHierarchyForm from "src/SiteAdmin/ProductHierarchy/ProductSelectProductHierarchyForm";

const COMBINED_FORM_TYPE = "RecipeDataSheet";
const COMBINED_FORM_RELATION_ID = "DefaultRecipe";
const FORMULATED_MATERIAL_ADMINISTRATOR = "Formulated Material Administrator";
const SITE_LEVEL_URL = "centric://REFLECTION/INSTANCE/_CS_Data/null";
const PROJECT_LINE_ITEMS = "ProjectLineItems";
const COMBINED_FORM_SUBTYPE = `${COMBINED_FORM_TYPE}/Subtype`;
const PRODUCT_HIERARCHY_DETAILS = "ProductHierarchyDetails";
const RECIPE_LINE_ITEM = "RecipeLineItem";
const HIERARCHY_TYPES: string[] = ["ProductHierarchy", "ProductHierarchyFolder"];
const RECIPE_SUBTYPE = "RecipeSubtype";

const FormulatedMaterialForm = declare(ProductSelectProductHierarchyForm, {
	isSubrecipe: false,
	productType: "FormulatedMaterial",

	/** recipeDataSheetRevisionUrl - URL of the parent RecipeDataSheetRevision */
	recipeDataSheetRevisionUrl: null,

	/** recipeLineItemClientNodeUrl - Client node URL for the intermediate RecipeLineItem on Recipe "New Special" action */
	recipeLineItemClientNodeUrl: null,

	/** defaultRecipeUrl - URL for the intermediate RecipeDataSheet on Copy action */
	defaultRecipeUrl: null,

	/** @property {C8Node} _hierarchyNode - node for top level hierarchy if in hierarchy */
	_hierarchyNode: null,

	/** @property {string} _nodeType - type of node that the view is located in */
	_nodeType: "",

	/** @property {Field} _recipe - the recipe selected */
	_recipe: null,

	/** @property {Field} _recipeSubtype - the recipe subtype */
	_recipeSubtype: null,


	/**
	 * @override
	 * Insert Subtype field if it hasn't been added to the FormDefinition and set Recipe as Combine Form
	 */
	_resolveView() {
		// Cache the Client node URL for Special Recipe Item creation
		const parentNode = this.cacheView.getNode(this.formInfo?.parentUrl);
		if (parentNode?.$Type == "RecipeDataSheetRevision") {
			this.recipeDataSheetRevisionUrl = this.formInfo.parentUrl;
			this.recipeLineItemClientNodeUrl = xml.getClientNodeUrl(RECIPE_LINE_ITEM);
		}

		const formulatedMaterialType = this.insertFieldIfNotExist(reflection.ATTR_SUBTYPE, 0);
		formulatedMaterialType.m_onChange = "onChangeFormulatedMaterialSubtype";

		this.combineForm(false, COMBINED_FORM_RELATION_ID, COMBINED_FORM_TYPE);
		const recipeNameFieldName = this.getCombinedFieldName(COMBINED_FORM_TYPE, "Node Name");
		this.insertFieldIfNotExist(this.getCombinedFieldName(COMBINED_FORM_TYPE, reflection.ATTR_SUBTYPE), recipeNameFieldName, {
			m_id: reflection.ATTR_SUBTYPE,
			m_path: pathCreate(COMBINED_FORM_RELATION_ID),
			combinedFormType: COMBINED_FORM_TYPE,
		});

		// Show Recipe Form Fields if Formulated Material being copied has Subrecipe subtype
		const sourceNode = this.defView.getSourceNode();
		const action = this.getSourceController().getAction()
			.getId();

		if (sourceNode) {
			this.setFieldDisabled(reflection.ATTR_SUBTYPE, true);
			this.onChangeFormulatedMaterialSubtype(this.defView, null, sourceNode.Subtype);
			// Cache Default Recipe node url (C8FB-443)
			this.defaultRecipeUrl = sourceNode?.DefaultRecipe;
		}

		this._nodeType = this.defView.getSource()?.getParent()?.$Type;
		const isProductHierarchy: boolean = HIERARCHY_TYPES.includes(this._nodeType);

		// Show ProductHierarchy field store if form is invoked on the site-level, project level, or product hierarchy
		if (this.formInfo?.contextUrl === SITE_LEVEL_URL || this.formInfo?.parentAttributeId === PROJECT_LINE_ITEMS || (isProductHierarchy && action !== "Edit")) {
			this.inherited(arguments);
		} else {
			Widget.prototype._resolveView.call(this);
		}
	},

	/**
	* @override
	* Hide all combinedFormType Form fields by default.
	*/
	_buildField(ctx: Context, field: Field, tr: HTMLElement, tdControl: HTMLElement) {
		var control = this.inherited(arguments);

		if (field.combinedFormType && !this.isSubrecipe) {
			this.setFieldHidden(field, true);
		}

		return control;
	},


	/**
	 * @override
	 * On Recipe's New Special action, attach form node to RecipeLineItem
	 */
	_getParentURLOfClientNode() {
		if (xml.isValidURL(this.recipeLineItemClientNodeUrl)) {
			return this.recipeLineItemClientNodeUrl;
		}
		return this.inherited(arguments);
	},

	/**
	* Display Recipe Form fields if Formulated Material type selected is Subrecipe.
	*/
	onChangeFormulatedMaterialSubtype(formView: FormView, field: Field, value: any) {
		this.isSubrecipe = !!config.getSubtypeSetting(value, "IsSubrecipe");

		formView.m_fields.forEach((otherField) => {
			if (otherField.combinedFormType) {
				this.setFieldHidden(otherField, !this.isSubrecipe);
			}
		});
	},

	/**
	 * @override
	 * On Recipe's New Special action, attach form client node to RecipeLineItem report node
	 */
	onWriteFormReport(field, writer, formInfo) {
		const { nodeUrl } = formInfo;

		// if creating Special FormulatedMaterial, then create path up to the data sheet revision
		if (xml.isValidURL(this.recipeDataSheetRevisionUrl) && xml.isValidURL(this.recipeLineItemClientNodeUrl)) {
			writer.createNode(this.recipeLineItemClientNodeUrl, RECIPE_LINE_ITEM)
				.attach(this.recipeDataSheetRevisionUrl, "Items")
				.changeAttribute("Actual", xml.ATTR_REF, nodeUrl)
				.changeAttribute("Copy", xml.ATTR_REF, nodeUrl);
		}

		// if creating a Copy of a Formualted Material with Sub-Recipe subtype,
		// have correct reference to Recipe Data Sheet (C8FB-443)
		if (xml.isValidURL(this.defaultRecipeUrl)) {
			writer.changeNode(nodeUrl)
				.changeAttribute("DefaultRecipe", xml.ATTR_REF, this.defaultRecipeUrl);
		}
	},

	/**
	 * @override
	 */
	getFieldStore(field, value) {
		// If node being copied has IsSubrecipe = TRUE, get field store by subtype instead of AggregateStore
		if (this.defView.getSourceNode() && field.getControlName() === COMBINED_FORM_SUBTYPE) {
			return config.getSubtypeStore(COMBINED_FORM_TYPE);
		}

		return this.inherited(arguments);
	},

	/**
	 * @override
	 */
	async onViewEnd(): Promise<void> {
		const currentNode = this.defView.getSource()?.getParent();

		if (!currentNode) {
			return;
		}

		if (HIERARCHY_TYPES.includes(this._nodeType) || this._nodeType == PRODUCT_HIERARCHY_DETAILS) {
			this._hierarchyParents = await this._getRootHierarchyNode(currentNode, []);
			this._currentLevel = currentNode.Level ? currentNode.Level : 0;
			this._hierarchyNode = this._hierarchyParents[0][0];

			if (this._hierarchyNode.$URL && HIERARCHY_TYPES.includes(this._nodeType)) {
				// With the 'quiet' parameter set to false, the setFieldValue call will trigger '_onChangeProductHierarchy'
				this.setFieldValue(this._fieldHierarchy, this._hierarchyNode.$URL, false);
				this.setFieldDisabled(this._fieldHierarchy, true);
			}
		}
	},

	async _getRootHierarchyNode(currentNode, hierarchyParents) {
		const subtype = await resolveNode(currentNode.Subtype);
		hierarchyParents = [...[[currentNode, subtype]], ...hierarchyParents];
		const currParents = currentNode.HierarchyParents;
		const parentURL = currentNode.__Parent__;
		if (xml.isEmptyURL(parentURL) || !currParents) {
			return hierarchyParents;
		}
		for (let i = currParents.length - 1; i >= 0; i--) {
			const currentOne = currParents[i];
			const currentFolderNode = await resolveNode(currentOne);
			const currentFolderSubtype = await resolveNode(currentFolderNode.Subtype);

			hierarchyParents = [...[[currentFolderNode, currentFolderSubtype]], ...hierarchyParents];
		}

		return hierarchyParents;
	},

	/**
	 * @override
	 * Override to filter store
	 * @param field
	 * @returns StoreNames
	 */
	getProductHierarchyFolderStore(field: Field) {
		const prevValue = this.getPrevLevelValue(field.m_name);
		const ctx = this.getContext();
		if (prevValue === csi.EMPTY_URL) {
			return ctx.view.createHeadingNamesStore(ctx, field, null);
		}
		const rel = lang.mixin(new Relationship(), {
			m_module: "SiteAdmin",
			m_operation: "ProductHierarchyLevelGetLeafFoldersWithRole",
			m_typeNode: "ProductHierarchyFolder",
			m_parameters: { URL: prevValue, Role: FORMULATED_MATERIAL_ADMINISTRATOR },
		});

		// create view for goto dialog
		const store = ctx.view.createHeadingNamesStore(ctx, field, null, rel);

		return store;
	},

});

export = FormulatedMaterialForm;